'use client'

import { getLanguageFromLocale } from '@hermes/translations'
import {
  BookIcon,
  Box,
  Logo,
  LogoMobile,
  Typography,
  useTheme,
  NoFlag,
  SeFlag,
  MenuItem,
  EnFlag
} from '@hermes/web-components'
import BookHeartIcon from '@hermes/web-components/src/icons/BookHeart'
import { useLocale } from '@hooks/useLocale'
import { navigationPaths, useNavigation } from '@hooks/useNavigation'
import useTranslateMessage from '@hooks/useTranslateMessage'
import { useUser } from '@lib/contexts/userContext'
import { Locale } from '@lib/utils/localeHelpers'
import getRegionFromLocale from '@lib/utils/getRegionFromLocale'
import Link from 'next/link'
import Skeleton from 'react-loading-skeleton'
import {
  CategoriesButtonContainer,
  HeaderAppBar,
  HeaderContainer,
  ItemsAreaContainer,
  ItemsList,
  LogoContainer,
  NavigationButton,
  SearchAreaContainer,
  HeaderCountrySelector,
  HeaderSelectorLabel,
  HeaderSelectorLabelIcon,
  HeaderMenuPaperStyles
} from './Header.styles'
import { SearchField } from './SearchField'
import UserArea from './User'

export const Header = ({ localeOverride }: { localeOverride?: Locale }) => {
  const theme = useTheme()
  const { down } = theme.breakpoints
  const localeFromHook = useLocale()
  const locale = localeOverride || localeFromHook
  const { user, isLoading } = useUser()
  const { t } = useTranslateMessage()
  const lang = getLanguageFromLocale(locale)
  const navigation = useNavigation()
  const region = getRegionFromLocale(locale)

  const languages = [
    { value: `en-${region}`, label: t({ id: 'lang.english' }), icon: () => EnFlag(), show: true },
    { value: 'no', label: t({ id: 'lang.norway' }), icon: () => NoFlag(), show: region === 'no' },
    { value: 'se', label: t({ id: 'lang.sweden' }), icon: () => SeFlag(), show: region === 'se' }
  ]

  const LanguageSelector = () => {
    const Label = (labelValue: string) => {
      const item = languages.find((language) => language.value === labelValue)
      return (
        <HeaderSelectorLabel>
          <HeaderSelectorLabelIcon>{item?.icon()}</HeaderSelectorLabelIcon>
        </HeaderSelectorLabel>
      )
    }

    return (
      <HeaderCountrySelector
        value={locale}
        MenuProps={{
          PaperProps: { sx: HeaderMenuPaperStyles }
        }}
        renderValue={(renderValue) => Label(String(renderValue))}
        onChange={(e) => {
          const pathArray = window.location.pathname.split('/')
          pathArray.shift()
          pathArray.shift()
          window.location.href = `${window.location.origin}/${e.target.value}/${pathArray.join('/')}`
        }}
      >
        {languages.map((item, index) => (
          <MenuItem key={index} value={item.value} disabled={!item.show || locale === item.value}>
            <HeaderSelectorLabelIcon>{item?.icon()}</HeaderSelectorLabelIcon>
            <Typography fontWeight={500} fontSize="14px" lineHeight="20px">
              {item.label}
            </Typography>
          </MenuItem>
        ))}
      </HeaderCountrySelector>
    )
  }

  const CategoriesButton = () => (
    <Link
      href={
        lang === 'no' ? `/${locale}${navigationPaths.explorePage()}` : `/${locale}/${navigationPaths.categoriesPage()}`
      }
      aria-label="explore"
    >
      <NavigationButton
        sx={{ [down('sm')]: { display: 'none' }, ...(lang !== 'no' ? { padding: '0 12px' } : {}), padding: '0px' }}
      >
        {lang === 'no' ? (
          <CategoriesButtonContainer>
            <BookHeartIcon style={{ position: 'relative', top: '0px' }} width="20px" />
            <Typography variant="body2">{t({ id: 'categories.title' })}</Typography>
          </CategoriesButtonContainer>
        ) : (
          <CategoriesButtonContainer>
            <BookIcon style={{ position: 'relative', top: '0px' }} width="20px" />
            <Typography variant="body2">{t({ id: 'categories' })}</Typography>
          </CategoriesButtonContainer>
        )}
      </NavigationButton>
    </Link>
  )

  return (
    <HeaderAppBar position="static">
      <HeaderContainer
        sx={{
          maxWidth: {
            xs: '100%',
            md: 'xl'
          }
        }}
      >
        <Link
          href={`/${locale}`}
          aria-label="home"
          onTouchStart={() => navigation.navigate.homePage()}
          prefetch
          style={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            zIndex: '10'
          }}
        >
          <LogoContainer forMobile={true} paddingRight={'16px'} paddingLeft={'16px'}>
            <LogoMobile />
          </LogoContainer>
          <LogoContainer ml="1px" mr="15px">
            <Logo width={143} height={44} />
          </LogoContainer>
        </Link>
        <SearchAreaContainer>
          <CategoriesButton />
          <SearchField />
        </SearchAreaContainer>
        <ItemsAreaContainer>
          <ItemsList>
            <NavigationButton sx={{ padding: '0 12px' }} onClick={() => navigation.navigate.addSalePage()}>
              <Typography variant="body2">{t({ id: 'header.sellBooks' })}</Typography>
            </NavigationButton>
            {isLoading ? (
              <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} suppressHydrationWarning>
                <Skeleton width={'300px'} height={'40px'} />
              </Box>
            ) : (
              <>
                {user && (
                  <NavigationButton
                    sx={{ padding: '0 12px', width: '150px' }}
                    onClick={() => navigation.navigate.messagesPage()}
                  >
                    <Typography variant="body2">{t({ id: 'messages' })}</Typography>
                  </NavigationButton>
                )}
                {!user && (
                  <NavigationButton sx={{ padding: '0 12px' }} onClick={() => navigation.navigate.authPage()}>
                    <Typography variant="body2">{t({ id: 'header.signInOrRegister' })}</Typography>
                  </NavigationButton>
                )}
              </>
            )}
          </ItemsList>
          <UserArea user={user} />
          <LanguageSelector />
        </ItemsAreaContainer>
      </HeaderContainer>
    </HeaderAppBar>
  )
}
